import React from "react";

import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";

import ModernModal from "../../../../common/ModernModal";
import {
  ModernFormAlert,
  ModernFormField,
} from "../../../../common/ModernForm";

interface RegenerateInsightsDialogProps {
  selectedTab: string;
  open: boolean;
  onClose: () => void;
  onRegenerate: (prompt: string) => void;
  hasCredits: boolean;
  loading?: boolean;
  customPrompt: string;
  setCustomPrompt: (prompt: string) => void;
}

export default function RegenerateInsightsDialog({
  selectedTab,
  open,
  onClose,
  onRegenerate,
  hasCredits,
  loading = false,
  customPrompt,
  setCustomPrompt,
}: RegenerateInsightsDialogProps) {
  const getPromptExamples = (tab: string) => {
    switch (tab) {
      case "tweets":
        return [
          "Generate 5 engaging tweets with relevant hashtags",
          "Create tweets focusing on actionable insights",
          "Make tweets more conversational and relatable",
        ];
      case "chapters":
        return [
          "Create detailed chapter summaries with timestamps",
          "Focus on key learning points in each chapter",
          "Generate chapter titles that are SEO-friendly",
        ];
      case "blog_post":
        return [
          "Write a comprehensive blog post with subheadings",
          "Focus on practical tips and takeaways",
          "Include a compelling introduction and conclusion",
        ];
      case "newsletter":
        return [
          "Create a newsletter format with clear sections",
          "Include actionable insights for subscribers",
          "Add a personal touch and call-to-action",
        ];
      case "quotes":
        return [
          "Extract the most impactful and memorable quotes",
          "Focus on inspirational and motivational quotes",
          "Include context for each quote",
        ];
      case "linkedin_posts":
        return [
          "Create professional LinkedIn posts with industry insights",
          "Focus on thought leadership and expertise",
          "Include relevant professional hashtags",
        ];
      case "titles":
        return [
          "Generate SEO-optimized titles",
          "Create attention-grabbing headlines",
          "Focus on titles that encourage clicks",
        ];
      case "keywords":
        return [
          "Extract industry-specific keywords",
          "Focus on trending and relevant terms",
          "Include long-tail keywords for SEO",
        ];
      case "descriptions":
        return [
          "Write compelling episode descriptions",
          "Focus on key benefits for listeners",
          "Include relevant keywords naturally",
        ];
      default:
        return ["Enter your custom prompt here"];
    }
  };

  const handleRegenerate = () => {
    onRegenerate(customPrompt);
  };

  const promptExamples = getPromptExamples(selectedTab);

  return (
    <ModernModal
      open={open}
      onClose={onClose}
      title={`Regenerate ${selectedTab.charAt(0).toUpperCase() + selectedTab.slice(1)}`}
      maxWidth="md"
      actions={
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRegenerate}
            disabled={!hasCredits || loading}
            sx={{
              background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
              boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                boxShadow: "0 6px 20px rgba(139, 92, 246, 0.4)",
                transform: "translateY(-1px)",
              },
              "&:disabled": {
                background: "#64748b !important",
                color: "lightgray !important",
                boxShadow: "none !important",
                transform: "none",
                border: "1px solid #cbd5e1 !important",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ color: "#fff" }} />
                <Typography variant="body1" sx={{ color: "#fff", ml: 1 }}>
                  Generating...
                </Typography>
              </>
            ) : (
              "Regenerate"
            )}
          </Button>
        </Box>
      }
    >
      {!hasCredits && (
        <Box sx={{ mb: 2 }}>
          <ModernFormAlert severity="error">
            No credits available. Please upgrade your plan to regenerate
            insights.
          </ModernFormAlert>
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Typography
          variant="body1"
          sx={{ color: "#374151", lineHeight: 1.6, fontSize: "1rem" }}
        >
          Enter a custom prompt to regenerate the {selectedTab} with specific
          requirements. Custom prompts help generate insights tailored to your
          exact needs.
        </Typography>

        <ModernFormField
          label="Custom AI Prompt (Optional)"
          name="customPrompt"
          multiline
          rows={4}
          value={customPrompt}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setCustomPrompt(e.target.value)
          }
          placeholder="Enter your custom instructions for the AI..."
        />

        <Typography
          variant="caption"
          sx={{ color: "#6b7280", fontStyle: "italic", fontSize: "0.875rem" }}
        >
          💡 Leave empty to use default parameters for this content type.
        </Typography>

        <Box>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, mb: 2, color: "#1f2937", fontSize: "1rem" }}
          >
            Example Prompts:
          </Typography>
          <List
            sx={{
              bgcolor: "#f8fafc",
              borderRadius: 2,
              py: 1,
              "& .MuiListItem-root": {
                py: 0.5,
              },
            }}
          >
            {promptExamples.map((example) => (
              <ListItem
                key={example}
                sx={{ cursor: "pointer" }}
                onClick={() => setCustomPrompt(example)}
              >
                <ListItemText
                  primary={example}
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.9rem",
                      color: "#374151",
                      fontWeight: 500,
                      "&:hover": {
                        color: "#1976d2",
                      },
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
          <Typography
            variant="caption"
            sx={{
              color: "#6b7280",
              mt: 1,
              display: "block",
              fontSize: "0.8rem",
            }}
          >
            Click any example to use it as your prompt
          </Typography>
        </Box>
      </Box>
    </ModernModal>
  );
}
